import {
  Flex,
  Icon,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import Button from 'common/components/button';
import FormError from 'common/components/form-error';
import Input from 'common/components/input';
import WrapInputLabel from 'common/components/wrap-input-label';
import ContactCard from 'components/shared/contact-card';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { FaCheck, FaExclamationCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUpdateSinglePlanMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { extractErrorMsg } from 'utils/errors';
import * as Yup from 'yup';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { ModalButtons } from 'common/components/modal-buttons';
import { planStatusEnum } from 'common/utils/enums';

const PlanIneligibleModal = ({ isOpen, onClose, planId }) => {
  const navigate = useNavigate();
  const [updatePlan, { isLoading }] = useUpdateSinglePlanMutation();
  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
  } = useUserTracker();

  const handleBackToDashboard = () => {
    onClose();
    navigate('/dashboard/plans?tab=quotes');
  };

  return (
    <Formik
      initialValues={{ preLeaseExpiration: '' }}
      validationSchema={Yup.object({
        preLeaseExpiration: Yup.date()
          .required('Required')
          .min(moment().startOf('day').toDate(), 'Date cannot be in the past'),
      })}
      onSubmit={(values) => {
        const submissionValues = {
          planId,
          status: planStatusEnum.NOT_ELIGIBLE,
          preLeaseExpiration: values.preLeaseExpiration,
        };
        trackFormSubmission(submissionValues);
        updatePlan(submissionValues)
          .unwrap()
          .then(() => {
            trackFormSubmissionSuccess(submissionValues);
            toast.success("We'll remind you about ReLease when your lease ends");
            handleBackToDashboard();
          })
          .catch((error) => {
            const errMsg = extractErrorMsg(
              error,
              'Could not save your lease end date.'
            );
            toast.error(errMsg);
            trackFormSubmissionError(submissionValues, errMsg);
          });
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values }) => (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          isCentered
          size={{ base: 'full', md: 'xl' }}
          closeOnOverlayClick={false}
        >
          <Form>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader className='font-medium text-xl inline-flex gap-3'>
                Your lease is not eligible for ReLease
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody className='pt-0 pb-6 flex flex-col gap-4'>
                <Text className='text-base'>
                  Unfortunately, ReLease is only available{' '}
                  <span className='font-semibold'>
                    up to 7 days after your lease signing date.
                  </span>
                </Text>

                <List className='flex flex-col -mt-2 gap-1'>
                  <ListItem className='inline-flex gap-1 items-center text-base'>
                    <ListIcon as={FaCheck} />
                    Please double-check your lease signing date to ensure it’s
                    correct.
                  </ListItem>
                  <ListItem className='inline-flex gap-1 items-center text-base'>
                    <ListIcon as={FaCheck} />
                    If you're applying for a lease renewal, please enter the
                    information for your renewal offer (not your current lease).
                  </ListItem>
                  <ListItem className='inline-flex gap-1 items-center text-base'>
                    <ListIcon as={FaCheck} />
                    If you believe you may qualify for an exception or need
                    assistance, our support team is here to help.
                  </ListItem>
                </List>

                <Text className='text-base'>
                  However, if you provide your lease end date, we can send you a
                  reminder to purchase when it's time for your next lease.
                </Text>

                <WrapInputLabel isRequired>
                  When does your current lease end?
                </WrapInputLabel>
                <Input
                  type='date'
                  name='preLeaseExpiration'
                  value={values.preLeaseExpiration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={moment().format('YYYY-MM-DD')}
                />
                <FormError name='preLeaseExpiration' />
              </ModalBody>
              <ModalButtons className='md:rounded-b-lg'>
                <Button
                  title='Back to Dashboard'
                  theme='secondary'
                  onClick={handleBackToDashboard}
                />
                <Button
                  title='Remind Me'
                  type='submit'
                  isLoading={isLoading}
                  onClick={handleSubmit}
                />
              </ModalButtons>
            </ModalContent>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default PlanIneligibleModal;
